import { useCallback, useEffect, useMemo, useRef } from 'react';

import isEqual from 'lodash/isEqual';
import { useTranslation } from 'react-i18next';

import { legacyConvertCurrency } from '@pkg/components/Sections/Groups/helpers';
import { useAsyncGetCompetitionCollectionsMetadata } from '@pkg/hooks';
import { getCriteriaTooltipCopy } from '@pkg/hooks/fantasyWishlists/lineup/utils';
import { Position } from '@pkg/types/enums';

import {
    CollectionChainType,
    type CompetitionCollectionAvailabilityItem,
    CompetitionCollectionStatus,
    Currency,
} from '@app/client';
import { type WishlistSummaryResponse } from '@app/client/resources/wishlists/types';
import { type LineupItemProps } from '@app/pages/FantasyWishlist/components/FantasySidebar/types';
import { type LineupItemsFormat, useGetLineupSummaryData } from '@app/pages/Wishlist/hooks';
import {
    useExchangeRatesStore,
    useFantasyCollectionMetadataStore,
    useFantasyCollectionValidityStore,
    useFantasyLineupStore,
    useFantasyMetadataStore,
    useFantasyTeamsStore,
} from '@app/store/zustand';

const formatLineup = (portfolioSummary: WishlistSummaryResponse): LineupItemsFormat[] => {
    return portfolioSummary.portfolio_summary?.interested_collections
        ?.filter((item) => item.position !== Position.Exited)
        ?.map((item) => {
            return {
                slug: item?.slug || '',
                quantity: item?.collection_count || 1,
                floorPrice: (
                    (item?.value_usd || item?.initial_value_usd || 0) / (item?.collection_count || 1)
                ).toString(),
                confirmed: item?.confirmed,
                chain: item?.network || CollectionChainType.Ethereum,
                position: item.position,
            };
        });
};

export const useUpdateSummaryStore = () => {
    const summaryTeamId = useFantasyTeamsStore((state) => state.summaryTeamId);
    const setSummaryTeamId = useFantasyTeamsStore((state) => state.setSummaryTeamId);
    const setCurrentUserLineupSummary = useFantasyLineupStore((state) => state.setCurrentUserLineupSummary);
    const setLineupItemsStore = useFantasyLineupStore((state) => state.setLineupItems);
    const currentUserLineupSummary = useFantasyLineupStore((state) => state.currentUserLineupSummary);

    const currentGameWishlistId = useFantasyMetadataStore((state) => state.currentGameWishlistId);
    const currentGame = useFantasyMetadataStore((state) => state.currentGame);

    const { data: summaryData } = useGetLineupSummaryData({
        disabled: !currentGameWishlistId,
        providedSlug: currentGameWishlistId || '',
        isCurrentUserLineup: true,
        intervalEnabled: true,
        ignorePreviousData: true,
    });

    const prevLineup = useRef<LineupItemsFormat[]>();

    useEffect(() => {
        if (summaryData && currentGameWishlistId) {
            const portfolioValue = // TODO remove once BE not returning negative vals
                summaryData?.portfolio_value_usd > 0
                    ? summaryData?.portfolio_value_usd
                    : (currentGame?.max_value || 0 - (summaryData?.budget_usd || 0)) > 0
                      ? currentGame?.max_value || 0 - (summaryData?.budget_usd || 0)
                      : 0;
            if (portfolioValue && currentUserLineupSummary?.portfolio_value_usd !== portfolioValue) {
                setCurrentUserLineupSummary(summaryData);
            }

            if (
                summaryData?.team_id &&
                summaryData?.portfolio_value_usd > 0 &&
                summaryTeamId !== summaryData?.team_id
            ) {
                setSummaryTeamId(summaryData?.team_id);
            }

            const lineupData = formatLineup({ ...summaryData, portfolio_value_usd: portfolioValue });
            const differentLineupData = lineupData?.length && !isEqual(prevLineup.current, lineupData);
            if (differentLineupData) {
                setLineupItemsStore(lineupData);
            }

            prevLineup.current = lineupData;
        } else {
            prevLineup.current = undefined;
        }
    }, [
        summaryData,
        currentGameWishlistId,
        currentUserLineupSummary?.portfolio_value_usd,
        summaryTeamId,
        setCurrentUserLineupSummary,
        setSummaryTeamId,
        setLineupItemsStore,
        currentGame?.max_value,
    ]);
};

export function useFantasyLineupListItems() {
    const { t: translate } = useTranslation('fantasy_wishlist');
    const lineupItems = useFantasyLineupStore((state) => state.lineupItems);
    const summaryData = useFantasyLineupStore((state) => state.currentUserLineupSummary);
    const setLineupListItems = useFantasyLineupStore((state) => state.setLineupListItems);
    const lineupListItems = useFantasyLineupStore((state) => state.lineupListItems);

    const collectionsMetadata = useFantasyCollectionMetadataStore((state) => state.collectionsMetadata);
    const collectionsValidity = useFantasyCollectionValidityStore((state) => state.collectionsValidity);
    const collectionsStatuses = useFantasyCollectionValidityStore((state) => state.collectionsStatuses);

    const convertCurrency = useExchangeRatesStore((state) => state.convertCurrency);

    const getStatusTooltip = useCallback(
        (collectionStatus: CompetitionCollectionStatus) => {
            const collectionStatusTooltipCopy = {
                Banned: translate('list_item_error_banned'),
                UnderInvestigation: translate('list_item_error_under_investigation'),
            };
            return collectionStatusTooltipCopy[collectionStatus];
        },
        [translate],
    );

    const getCriteriaTooltip = useCallback(
        (collectionCriteria: CompetitionCollectionAvailabilityItem) => {
            if (collectionCriteria) {
                return getCriteriaTooltipCopy(collectionCriteria, translate);
            }
        },
        [translate],
    );

    const getCurrency = (chain: CollectionChainType) => {
        switch (chain) {
            case CollectionChainType.Aptos:
                return 'apt';
            case CollectionChainType.Polygon:
                return 'matic';
            case CollectionChainType.Ethereum:
            default:
                return 'eth';
        }
    };

    const lineupList = useMemo(() => {
        return lineupItems.map((lineupItem) => {
            const { slug, quantity, tooltipMessage, confirmed, chain, floorPrice, position } = lineupItem;
            const metadataChain = collectionsMetadata?.[chain.toLowerCase()];

            const collectionData = metadataChain?.find((col) => col.slug === lineupItem.slug);
            const metadata = collectionData?.metadata;

            const collectionCriteria = collectionsValidity[slug];
            const isValid = collectionCriteria?.is_valid;
            const status = collectionsStatuses[slug] || CompetitionCollectionStatus.Normal;

            const updatedTooltipMessage =
                status !== CompetitionCollectionStatus.Normal
                    ? getStatusTooltip(status)
                    : !isValid
                      ? getCriteriaTooltip(collectionCriteria)
                      : '';

            const summaryItem = summaryData?.portfolio_summary.interested_collections?.find(
                (item) => item?.slug === slug,
            );

            const summaryFloor =
                summaryItem?.value && summaryItem?.collection_count
                    ? summaryItem?.value / summaryItem?.collection_count
                    : 0;

            let collectionDataFloorPrice = legacyConvertCurrency(
                parseFloat(collectionData?.floor_price_wei || '0'),
                Currency.WEI,
                Currency.ETH,
            );

            if (chain === CollectionChainType.Aptos) {
                collectionDataFloorPrice = legacyConvertCurrency(
                    parseFloat(collectionData?.floor_price || '0'),
                    Currency.OCT,
                    Currency.APT,
                );
            }

            const summaryCurrentFloor = summaryFloor || lineupItem.floorPrice || collectionDataFloorPrice || 0;

            const currency = getCurrency(chain);
            const floorPriceUsd =
                Number(floorPrice) || convertCurrency(currency, 'usd', parseFloat(summaryCurrentFloor.toString()));

            const thumbnail = metadata?.image_url;
            const name = metadata?.name || collectionData?.collection_name;
            const totalSupply = collectionData?.tokenCount || metadata?.total_supply;

            return {
                slug,
                chain,
                id: slug,
                name,
                thumbnail,
                collectionSlug: slug,
                loading: !metadata,
                quantity: quantity || 1,
                unitPrice: floorPriceUsd,
                isValid,
                status,
                tooltipMessage: updatedTooltipMessage || tooltipMessage,
                confirmed,
                startValueUsd: summaryItem?.initial_value_usd,
                totalSupply,
                totalPrice: floorPriceUsd * (quantity || 1),
                currentFloor: summaryCurrentFloor,
                position,
            } as LineupItemProps;
        });
    }, [
        collectionsMetadata,
        collectionsStatuses,
        collectionsValidity,
        getCriteriaTooltip,
        getStatusTooltip,
        lineupItems,
        summaryData?.portfolio_summary.interested_collections,
        convertCurrency,
    ]);

    useEffect(() => {
        if (lineupList) {
            if (!lineupListItems || !isEqual(lineupList, lineupListItems)) {
                setLineupListItems(lineupList);
            }
        }
    }, [lineupList, lineupListItems, setLineupListItems]);

    const missingMetadataSlugs = useMemo(
        () =>
            lineupList
                .filter((item) => item.loading)
                .map((item) => {
                    return `${item.chain.toLowerCase() || 'ethereum'}.${item.slug}`;
                }),
        [lineupList],
    );
    const prevMissingCollections = useRef<string[]>([]);

    const slugsAreEqualOrEmpty = useMemo(() => {
        return Boolean(
            !missingMetadataSlugs ||
                isEqual(prevMissingCollections.current, missingMetadataSlugs) ||
                (!prevMissingCollections?.current?.length && !missingMetadataSlugs?.length),
        );
    }, [missingMetadataSlugs]);

    const updateCollectionsData = useAsyncGetCompetitionCollectionsMetadata({
        setMetadataStore: true,
        disabled: slugsAreEqualOrEmpty || !lineupItems || !lineupListItems,
    });

    useEffect(() => {
        if (!slugsAreEqualOrEmpty) {
            updateCollectionsData(missingMetadataSlugs).then((r) => {
                if (r?.ok) {
                    prevMissingCollections.current = missingMetadataSlugs;
                }
            });
        }
    }, [missingMetadataSlugs, slugsAreEqualOrEmpty, updateCollectionsData]);
}
